import { type LucideIcon } from "lucide-react";
import { cn } from "../../lib/utils";

/**
 * EmptyState
 * Anatomy: 40px icon tile (radius 8px, muted surface) + 16px semibold title
 * + 14px tertiary description + optional recovery action.
 * Always states the CAUSE and provides ONE recovery action.
 */
interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center py-12 px-6 text-center",
        className,
      )}
    >
      {/* 40px icon tile */}
      <div
        className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg bg-soft text-ink-3"
        aria-hidden="true"
      >
        <Icon size={20} strokeWidth={1.5} />
      </div>

      {/* 16px semibold title */}
      <p className="text-base font-semibold text-ink">{title}</p>

      {/* 14px tertiary description */}
      <p className="mt-1 max-w-xs text-sm text-ink-3">{description}</p>

      {/* Recovery action */}
      {action && (
        <button
          onClick={action.onClick}
          className={cn(
            "mt-4 inline-flex h-8 items-center rounded-lg border border-line px-4",
            "text-sm font-medium text-ink-2",
            "hover:border-ink-3 hover:text-ink transition-colors duration-normal",
          )}
        >
          {action.label}
        </button>
      )}
    </div>
  );
}
